import { IMessageProps } from 'Components/Core/Message';
import { compare } from 'Data/ViewModels/Core/Severity';
import { IEquipmentDataModel, isIEquipmentDataModel } from 'Data/ViewModels/Equipment';
import { Limit, Message } from 'Data/ViewModels/IApiDataModel';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { joinPaths, pathAsArray } from 'Lib/Object/Path/shared';
import { isNil, sort } from 'ramda';
import * as objectPath from '../../../../Lib/Object/Path/Immutable';

/**
 * Creates a function that will get the first message for a given property and id from a supplied messages array.
 */
export const createMessageGetter =
  (propertyName: string, id: string | undefined = undefined) =>
  (messages: Message[]) =>
    messages?.find((m) => m.PropertyName === propertyName && (isNil(id) || m.Id === id));

/**
 * Converts a message model to properties for a message component
 */
export const toMessageProps = (message: Message): IMessageProps => ({
  severity: message.MessageType,
  message: message.Text,
});

/**
 * Creates a function that will get all messages for a given property and id from a supplied messages array.
 */
export const createMessagesGetter =
  (propertyName: string, id: string | undefined = undefined) =>
  (messages: Message[]) =>
    messages?.filter((m) => m.PropertyName === propertyName && (isNil(id) || m.Id === id));

/**
 * Returns all messages for the given property paths
 *
 * This will include both messages returned via the API and client-only messages constructed from other data.
 */
export const getMessagesForMultiplePaths = (worksheet: any, paths: string[]): IMessageProps[] => {
  const onlyUnique = (value: IMessageProps, index: number, arr: IMessageProps[]) => {
    return arr.map((message) => message.message).indexOf(value.message) === index;
  };

  const messages =
    paths &&
    paths
      // shouldn't be necessary if the caller uses types because paths can't be nil
      .filter((path) => !isNil(path))
      .map((path) => getMessagesForPath(worksheet, path))
      .flat()
      .filter(onlyUnique);

  return messages ?? [];
};

/**
 * Returns all messages for a given property path
 *
 * This will include both messages returned via the API and client-only messages constructed from other data.
 */
export const getMessagesForPath = (worksheet: any, path: string): IMessageProps[] => {
  const pathArray = pathAsArray(path);
  const propName = pathArray.pop();

  // Obtain messages from API model
  pathArray.push('Messages');
  const messages = sort(
    (a: IMessageProps, b: IMessageProps) => compare(b.severity, a.severity),
    objectPath
      .get<Message[]>(worksheet, pathArray)
      ?.filter((m) => m.PropertyName === propName)
      .map<IMessageProps>((m) => ({ message: m.Text, severity: m.MessageType, id: joinPaths(path, m.Id) })) ?? []
  );

  return messages.concat(getLimitMessagesForPath(worksheet, path)).concat(getEquipmentMessagesForPath(worksheet, path));
};

/**
 * Returns messages related to limits for a given property path
 *
 * This will include additional messages returned via the API and client-only messages constructed from the limit data.
 */
const getLimitMessagesForPath = (worksheet: any, path: string): IMessageProps[] => {
  const pathArray = pathAsArray(path);
  const propName = pathArray.pop();

  pathArray.push('Limits');
  const limit = objectPath.get<Array<Limit>>(worksheet, pathArray)?.find((m) => m.PropertyName === propName);

  let limitMessages: Array<IMessageProps> = [];

  if (hasNonEmptyValue(limit)) {
    limitMessages.push({
      severity: limit?.IsPassing ? 'info' : 'error',
      message: `Limit: ${limit?.Text}`,
    });

    if (hasNonEmptyValue(limit!.Message)) {
      limitMessages.push(toMessageProps(limit!.Message));
    }
  }

  return limitMessages;
};

/**
 * Returns messages related to equipment for a given property path
 *
 * This covers client-only messages constructed from the equipment data.
 */
const getEquipmentMessagesForPath = (worksheet: any, path: string): IMessageProps[] => {
  const value = objectPath.get(worksheet, path);
  return isIEquipmentDataModel(value) ? getEquipmentMessages(value) : [];
};

/**
 * Returns messages related to equipment given an equipment data model.
 *
 * This covers client-only messages constructed from the equipment data.
 */
export const getEquipmentMessages = (equipment: IEquipmentDataModel): IMessageProps[] => {
  if (!hasValue(equipment) || !isIEquipmentDataModel(equipment)) return [];

  const equipmentMessageDefinitions: Array<{
    Message: IMessageProps;
    AppliesTo: (equipment: IEquipmentDataModel) => boolean;
  }> = [
    {
      Message: {
        severity: 'warning',
        message: 'No valid calibration found for the test date.',
      },
      AppliesTo: (e) => !e.Status.InCalibration && !e.Status.OutOfCalibration,
    },
    {
      Message: {
        severity: 'warning',
        message: 'This item is not in calibration for the test date.',
      },
      AppliesTo: (e) => e.Status.OutOfCalibration,
    },
  ];

  return equipmentMessageDefinitions.filter((m) => m.AppliesTo(equipment)).map((m) => m.Message);
};

/**
 * Takes two path strings and returns whether they match by comparing the elements of the path at each index.
 * @param {string} messagePath The path of the message.
 * @param {string} comparisonPath The path that will be used for comparison. This can be an actual path of an object or an abstract description of a path. The final element in the path can be the wildcard '*' so that it matches any element.
 */
export const messagePathsMatch = (messagePath: string, comparisonPath: string) => {
  const messagePathElements = pathAsArray(messagePath);
  const comparisonPathElements = pathAsArray(comparisonPath);
  if (messagePathElements.length !== comparisonPathElements.length) return false;
  for (let i = 0; i < messagePathElements.length; ++i) {
    if (
      messagePathElements[i] === comparisonPathElements[i] /* path matches at this level */ ||
      (comparisonPathElements[i] === '*' &&
        i === comparisonPathElements.length - 1) /* or this is a wildcard at the final position */
    ) {
      /* might still match, continue to check next part of path */
    } else {
      return false;
    }
  }
  return true;
};
